import {
  Component,
  computed,
  DOCUMENT,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  output,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import * as _ from 'lodash';
import moment from 'moment';
import { combineLatest, EMPTY, from, Observable, of, Subject, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  share,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { v4 as uuidV4 } from 'uuid';

import { ChartTabs, ExchangeCountriesCodes, Features, UserSettings, WatchlistType } from '@const';
import { roiPartLegendForTooltip } from '@constants/tooltips';
import { ObservableService } from '@core1/directives/observable.service';
import { Flags, SmileyListType } from '@mod/symbol-smiley/symbol-smiley.model';
import { DialogsService } from '@s/common';
import { EditionsService } from '@s/editions.service';
import { HistoricalDataService } from '@s/historical-data.service';
import { LiveDataService } from '@s/live-data.service';
import { ObservableService as ObservableService2 } from '@s/observable.service';
import { SecurityDataDetailsService } from '@s/security-data-details.service';
import { ISymbol, SymbolsService } from '@s/symbols.service';
import { UserDataService } from '@s/user-data.service';
import {
  DataWindowUpdateSource,
  IDataWindowUpdateCommand,
  ISymbolDetailsV2WithSource,
  IWheelFilter,
  IWheelStockOption,
  IWheelStockOptionRaw,
  WheelViewModes,
} from '@t/wheel/wheel.types';
import { convertToEasternTime } from '@u/utils';
import { WheelCalculatorService } from '../wheel-calculator/wheel-calculator.service';
import { WheelScannerService } from '../wheel-scanner-tab/wheel-scanner.service';
import { WheelService } from '../wheel.service';
import { CustomTable, IStockSubscription } from './wheel-data-window.model';

@Component({
  selector: 'app-wheel-datawindow',
  templateUrl: './wheel-datawindow.component.html',
  styleUrls: ['./wheel-datawindow.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class WheelDatawindowComponent implements OnInit, OnDestroy {
  public readonly openNews = output<void>();

  // details for sell-put options table
  protected sources: Array<'A' | 'C' | null> = [];
  protected strikePrice: CustomTable[] = [];
  protected premium: CustomTable[] = [];
  protected expiration: CustomTable[] = [];
  protected drop: CustomTable[] = [];
  protected dte: CustomTable[] = [];
  protected buyingPower: CustomTable[] = [];
  protected annularized: CustomTable[] = [];
  protected delta: CustomTable[] = [];

  protected colGroupsByDate: number[] = [];

  protected readonly chartTabs = ChartTabs;

  protected scannerResults: IWheelStockOptionRaw[] = [];
  protected subscribedStock: IStockSubscription = null;

  protected selectedOptionTabIndex = 1;
  private saveOptionsTabTimeout: NodeJS.Timeout | null = null;
  private saveShowSellPutMinStrikeLineTimeout: NodeJS.Timeout | null = null;
  private saveShowSellCallMinStrikeLineTimeout: NodeJS.Timeout | null = null;

  protected securityDetails$: Observable<ISymbolDetailsV2WithSource | null> = combineLatest([
    this.wheelService.lastReceivedSecurityDetails$,
    this.wheelService.updateDataWindowSymbol$,
  ]).pipe(
    switchMap(([securityDetailsMap, symbolUpdate]) => {
      if (!securityDetailsMap || !symbolUpdate.symbol.id || !securityDetailsMap[symbolUpdate.symbol.id]) {
        return combineLatest([
          from(this.symbolsService.getById(symbolUpdate.symbol.id)),
          from(this.securityDataDetailsService.get(symbolUpdate.symbol.id)),
          from(this.historicalDataService.get(symbolUpdate.symbol.id)),
        ]).pipe(
          map(([symbol, security, historicalData]) => {
            const lastTradePrice = historicalData.length ? historicalData[historicalData.length - 1].close : null;

            return {
              atr: undefined,
              close: null,
              close_40_min: null,
              company: symbol.description,
              created_date: symbol.last_updated?.toString(),
              dividends_count: null,
              drop_ratio: null,
              exchange: symbol.exchange_name,
              exchange_code: symbol.exchange_code,
              country_code: symbol.country_code,
              industry: security.industry_name,
              is_scanner: false,
              last_traded_price: lastTradePrice,
              long_description: security.long_description,
              maxAggressivePrice: null,
              maxConservativePrice: null,
              rsi: null,
              sector: security.sector_name,
              security_id: security.security_id,
              symbol: security.symbol,
              isFromScanner: false, // add source marker
              market_cap: security.market_cap,
              pe_ratio: security.pe_ratio,
              dividend_yield: security.dividend_yield,
              earnings_share: security.earnings_share,
              percent_insiders: security.percent_insiders,
              percent_institutions: security.percent_institutions,
              analyst_consensus_rating: null,
              price_to_avg_target_percent: null,
            };
          }),
        );
      }

      // send request to historical-data, take only latest with debounce-time

      return of({
        ...securityDetailsMap[symbolUpdate.symbol.id],
        isFromScanner: true, // add source marker
      });
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected lastTradePrice = 0;
  protected roiLowerBound = 30;
  protected roiUpperBound = 40;

  protected readonly securityDetailsSignal = toSignal(this.securityDetails$, { injector: this.injector });

  protected existedFilters: IWheelFilter;

  protected readonly sourcesTooltipText = { A: 'Aggressive', C: 'Conservative' };
  protected readonly features = Features;

  private readonly subscriber = new Subscription();
  private readonly addSellPutButtonClicked$ = new Subject<number>();
  private readonly addSellCallButtonClicked$ = new Subject<number>();

  private mobileWidth = 992;
  private isMobile = false;
  private width: number = window.innerWidth;

  private readonly saveDataWindowSource$ = new Subject<DataWindowUpdateSource>();
  private readonly wheelROIRangeSubscription: Subscription = null;

  // for symbol-details-panel
  protected readonly currentSymbol$ = this.securityDetails$.pipe(
    map((details) => (details ? details.security_id : null)),
    share(),
  );
  protected readonly securityDetailsMap$ = this.wheelService.lastReceivedSecurityDetails$;
  protected readonly watchlistTypes = WatchlistType;
  protected readonly smileyListType = SmileyListType;

  protected readonly wheelShowSellPutMinStrikeLine = toSignal(this.observableServiceV2.wheelShowSellPutMinStrikeLine, {
    injector: this.injector,
  });
  protected readonly wheelShowSellCallMinStrikeLine = toSignal(
    this.observableServiceV2.wheelShowSellCallMinStrikeLine,
    { injector: this.injector },
  );

  protected readonly sellCallOptionsTable = computed(() => {
    const { extendedOptions, priceMode } = this.scannerService.sellCallOptionsExtended();
    const sortedResult = _.orderBy(extendedOptions, ['expiration', 'strike_price'], ['asc', 'desc']);
    let colGroupsByDate = [];
    const scannerResults = [...sortedResult];

    const strikePrice = [];
    const premium = [];
    const expiration = [];
    const annularized = []; // ROI
    const delta = [];
    const gainPercent = [];

    if (scannerResults && scannerResults.length > 0) {
      scannerResults.forEach((d) => {
        strikePrice.push(new CustomTable(d.strike_price));
        delta.push(new CustomTable(d.delta));
        premium.push(new CustomTable(d.option_premium, d.min));
        annularized.push(new CustomTable(d.annularized));
        expiration.push(new CustomTable(moment(d.expiration).format('MMM D')));
        gainPercent.push(new CustomTable(d.gainPercent));
      });

      colGroupsByDate = this.getColGroups(expiration.map((exp) => exp.value));
    }

    return {
      priceMode,
      scannerResults,
      table: {
        colGroupsByDate,
        strikePrice,
        premium,
        expiration,
        annularized,
        delta,
        gainPercent,
      },
    };
  });

  protected readonly roiLegend = `
    The annualized ROI for the credit received.
    ${roiPartLegendForTooltip}
  `;

  private prevSymbolId: number = null;
  protected isContentBlurred = this.observableServiceV2.isStrategiesContentBlurred.getValue();
  private isMaxColumnDialogOpen = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private securityDataDetailsService: SecurityDataDetailsService,
    private symbolsService: SymbolsService,
    private scannerService: WheelScannerService,
    private injector: Injector,
    private wheelCalculatorService: WheelCalculatorService,
    private wheelService: WheelService,
    private observableService: ObservableService,
    private observableServiceV2: ObservableService2,
    private userDataService: UserDataService,
    private liveDataService: LiveDataService,
    private historicalDataService: HistoricalDataService,
    private editionsService: EditionsService,
    private dialogsService: DialogsService,
  ) {
    this.observableService.wheelFilters.subscribe((res) => {
      this.existedFilters = res;
    });
    this.wheelROIRangeSubscription = this.observableService.wheelRoiRange.subscribe((res) => {
      this.roiLowerBound = res.lower;
      this.roiUpperBound = res.upper;
    });
  }

  async ngOnInit(): Promise<void> {
    this.subscriber.add(
      this.observableServiceV2.isStrategiesContentBlurred.subscribe((isBlurred) => {
        this.isContentBlurred = isBlurred;
      }),
    );

    this.subscriber.add(
      this.currentSymbol$.subscribe((securityId) => {
        if (securityId === this.prevSymbolId) {
          return;
        }

        this.observableServiceV2.isStrategiesContentBlurred.next(true);
        this.prevSymbolId = securityId;
      }),
    );

    this.subscriber.add(
      this.addSellPutButtonClicked$.pipe(debounceTime(1000)).subscribe((data) => {
        this.addSellPutOption(data);
      }),
    );

    this.subscriber.add(
      this.addSellCallButtonClicked$
        .pipe(
          tap(() => {
            // Trigger save calc to avoid losing data, before adding new one
            this.wheelService.onTriggerToSaveCalc$(true);
          }),
          debounceTime(1000),
        )
        .subscribe((data) => {
          this.addSellCallOption(data);
        }),
    );

    this.selectedOptionTabIndex = this.observableServiceV2.wheelOptionsTabIndex.getValue();

    try {
      this.existedFilters = JSON.parse(localStorage.getItem('wheelFilter'));
    } catch (error) {
      console.error(`WHEEL :: Something went wrong on restoring wheel filters, error: ${error}`);
    }

    this.subscriber.add(
      this.securityDetails$
        .pipe(
          distinctUntilChanged((v1, v2) => v1?.security_id === v2?.security_id),
          switchMap((securityDetails) => (securityDetails ? from(this.updateSymbolData(securityDetails)) : EMPTY)),
        )
        .subscribe(),
    );

    this.subscriber.add(
      combineLatest([this.wheelService.updateDataWindowFromScanner$, this.observableService.wheelFilters])
        .pipe(withLatestFrom(this.wheelService.updateDataWindowSymbol$, this.scannerService.lastReceivedScannerData$))
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .subscribe(([[updateSignal, filters], symbolData, scannerData]) => {
          this.handleReceivedSymbolData(symbolData, scannerData, filters);
        }),
    );

    this.wheelService.updateStockList$.next(); // activate combineLatest

    this.subscriber.add(
      this.wheelService.updateDataWindowSymbol$
        .pipe(
          withLatestFrom(
            this.observableService.wheelFilters,
            this.scannerService.smileyTrigger$,
            this.scannerService.lastReceivedScannerData$,
          ),
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .subscribe(async ([symbolData, filters, smiley, scannerData]) => {
          this.handleReceivedSymbolData(symbolData, scannerData, filters);
          this.saveDataWindowSource$.next(symbolData.source);
        }),
    );

    // should be preloaded: wheelDWSUpdateSource, wheelFilters
    // receive last used source of updating
    this.observableServiceV2.wheelDataWindowUpdateSource.pipe(take(1)).subscribe(async (wheelDWSUpdateSource) => {
      const symbolId = this.observableServiceV2.wheelSymbol.getValue();

      if (
        wheelDWSUpdateSource === DataWindowUpdateSource.ScannerConservative ||
        wheelDWSUpdateSource === DataWindowUpdateSource.ScannerAggressive
      ) {
        this.scannerService.firstScannerResponseReceived
          .pipe(
            switchMap(() => this.scannerService.lastReceivedScannerData$.asObservable()),
            take(1),
            withLatestFrom(this.observableService.wheelFilters),
          )
          .subscribe(async ([data, filters]) => {
            const riskType =
              wheelDWSUpdateSource === DataWindowUpdateSource.ScannerConservative ? 'conservative' : 'aggressive';

            const symbolOptionsFromScanner = this.scannerService.filterStockOptions(
              data.filter((item) => item.security_id === symbolId && item.atr_type === riskType),
              filters,
              this.scannerService.uniqueListOfExpiration$.getValue(),
            );

            if (symbolOptionsFromScanner.length === 0) {
              this.wheelService.updateDataWindowSymbol$.next({
                source: DataWindowUpdateSource.ChartSearchBar,
                symbol: { id: symbolId, name: '' },
              });

              return;
            }

            this.wheelService.updateDataWindowSymbol$.next({
              source: wheelDWSUpdateSource,
              symbol: { id: symbolId, name: '', options: data },
            });
          });
      } else {
        this.wheelService.updateDataWindowSymbol$.next({
          source: DataWindowUpdateSource.ChartSearchBar,
          symbol: { id: symbolId, name: '' },
        });
      }
    });

    this.subscriber.add(
      this.saveDataWindowSource$.pipe(distinctUntilChanged()).subscribe(async (source) => {
        await this.userDataService.set(UserSettings.WheelDataWindowUpdateSource, source);
      }),
    );

    const wheelROIRanges = this.observableServiceV2.wheelRoiRange.getValue();
    this.roiLowerBound = wheelROIRanges.lower;
    this.roiUpperBound = wheelROIRanges.upper;
  }

  ngOnDestroy(): void {
    if (this.subscribedStock) {
      this.liveDataService.unsubscribe(this.subscribedStock.subscriptionId, this.subscribedStock.symbol);
    }

    if (this.wheelROIRangeSubscription) {
      this.wheelROIRangeSubscription.unsubscribe();
    }

    this.subscriber.unsubscribe();
  }

  public onAddSellPutOption(index: number): void {
    this.addSellPutButtonClicked$.next(index);
  }

  protected onSelectSymbolFromDataWindow(symbol: ISymbol): void {
    if (symbol.country_code !== ExchangeCountriesCodes.US) {
      return;
    }

    // apply for data-window
    this.wheelService.updateDataWindowSymbol$.next({
      symbol: { id: symbol.security_id, name: symbol.symbol },
      source: DataWindowUpdateSource.ChartSearchBar,
    });

    this.observableServiceV2.wheelSymbol.next(symbol.security_id);
    this.userDataService.set(UserSettings.WheelSymbol, symbol.security_id);
  }

  protected onLastTradedPriceUpdate(value: { security_id: number; price: number }): void {
    const current = this.scannerService.lastTradePrice();
    if (current?.security_id === value.security_id && current?.price === value.price) {
      return;
    }
    this.scannerService.lastTradePrice.set({ security_id: value.security_id, price: value.price });
  }

  protected redirectToExpectedMoveDemoPage(): void {
    this.editionsService.redirectToDemoPage(Features.ExpectedMove);
  }

  protected onAddSellCallOption(index: number): void {
    this.addSellCallButtonClicked$.next(index);
  }

  protected addSellCallOption(index: number): void {
    this.saveSelectedTabToDB();

    const sellCallOptionsTableDetails = this.sellCallOptionsTable();
    const securityDetails = this.securityDetailsSignal();
    const stockPrice = sellCallOptionsTableDetails.scannerResults[index].stockPriceForCalculations;
    const userId = JSON.parse(localStorage.getItem('user')).userId;

    const details = {
      expiration: sellCallOptionsTableDetails.scannerResults[index].expiration,
      strikePrice: sellCallOptionsTableDetails.table.strikePrice[index].value,
      premium: sellCallOptionsTableDetails.table.premium[index].value,
      stockPrice,
    };

    this.wheelCalculatorService
      .saveStocksToDB({
        userId: userId,
        callSymbol: securityDetails.symbol,
        callStockPrice: stockPrice,
        callStrikePrice: details.strikePrice,
        callOptionPremium: details.premium,
        callExpiration: convertToEasternTime(details.expiration).unix(),
        callShares: 100,
        data: [],
      })
      .subscribe(
        async (res) => {
          this.wheelService.setWheelCalcData(null);
          this.wheelService.setWheelDataWindow(null);
          this.scannerService.onSelectChart$.next(true);
          this.wheelCalculatorService.setRefreshStock$(res.result);
        },
        (error) => {
          if (error.error.description === 'Your wheel-calculate limit cross.') {
            this.showMaxColumnDialog();
          }
        },
      );
  }

  protected onShowSellPutMinStrikeLineChange(value: boolean): void {
    this.observableServiceV2.wheelShowSellPutMinStrikeLine.next(value);

    if (this.saveShowSellPutMinStrikeLineTimeout) {
      clearTimeout(this.saveShowSellPutMinStrikeLineTimeout);
    }

    this.saveShowSellPutMinStrikeLineTimeout = setTimeout(async () => {
      await this.userDataService.set(UserSettings.WheelShowSellPutMinStrikeLine, value, false);
    }, 800);
  }

  protected onShowSellCallMinStrikeLineChange(value: boolean): void {
    this.observableServiceV2.wheelShowSellCallMinStrikeLine.next(value);

    if (this.saveShowSellCallMinStrikeLineTimeout) {
      clearTimeout(this.saveShowSellCallMinStrikeLineTimeout);
    }

    this.saveShowSellCallMinStrikeLineTimeout = setTimeout(async () => {
      await this.userDataService.set(UserSettings.WheelShowSellCallMinStrikeLine, value, false);
    }, 800);
  }

  protected optionsTabChanged(event: number): void {
    this.selectedOptionTabIndex = event;

    if (this.saveOptionsTabTimeout) {
      clearTimeout(this.saveOptionsTabTimeout);
    }

    this.saveOptionsTabTimeout = setTimeout(async () => {
      this.observableServiceV2.wheelOptionsTabIndex.next(event);
      await this.userDataService.set(UserSettings.WheelOptionsTabIndex, event);
    }, 800);
  }

  protected onChangeVisibility(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { target, visible }: { target: Element; visible: boolean },
    type: 'top' | 'bottom',
  ): void {
    const dataWindow = document.getElementById('data-window-container');

    if (!dataWindow) {
      return;
    }

    const className = type === 'top' ? 'shadow-top' : 'shadow-bottom';

    if (visible) {
      dataWindow.classList.remove(className);
    } else {
      dataWindow.classList.add(className);
    }
  }

  private async handleReceivedSymbolData(
    symbolData: IDataWindowUpdateCommand,
    scannerData: Array<IWheelStockOption>,
    filters: IWheelFilter,
  ): Promise<void> {
    if (symbolData.source === DataWindowUpdateSource.ScannerAggressive) {
      const filteredOptions = this.scannerService.filterStockOptions(
        scannerData.filter(
          (item) => item.security_id === symbolData.symbol.id && item.atr_type === 'aggressive' && item.is_scanner,
        ),
        filters,
        this.scannerService.uniqueListOfExpiration$.getValue(),
      );

      this.updateOptionsTable(filteredOptions); // use received options from scanner
      this.wheelService.optionsForMinStrike$.next(filteredOptions);
    } else if (symbolData.source === DataWindowUpdateSource.ScannerConservative) {
      const filteredOptions = this.scannerService.filterStockOptions(
        scannerData.filter(
          (item) => item.security_id === symbolData.symbol.id && item.atr_type === 'conservative' && item.is_scanner,
        ),
        filters,
        this.scannerService.uniqueListOfExpiration$.getValue(),
      );

      this.wheelService.optionsForMinStrike$.next(filteredOptions);
      this.updateOptionsTable(filteredOptions); // use received options from scanner
    } else {
      const filteredOptions = this.scannerService.filterStockOptions(
        scannerData.filter((item) => item.security_id === symbolData.symbol.id),
        filters,
        this.scannerService.uniqueListOfExpiration$.getValue(),
      );

      // apply filter by earn-before-exp for data for symbol selected from search-bar
      const fromSearch = filteredOptions.map((item) => ({
        ...item,
        atr_type:
          item.is_scanner && item.atr_type
            ? item.atr_type // show A/C for results from scanner
            : null,
      }));

      this.wheelService.optionsForMinStrike$.next(fromSearch);
      this.updateOptionsTable(fromSearch);
    }
  }

  private updateOptionsTable(options: Array<IWheelStockOptionRaw>): void {
    const result = options ? this.addFlagToDataObs(options) : [];
    const sortedResult = _.sortBy(result, ['expiration', 'strike_price']);

    this.scannerResults = Object.assign([], sortedResult);
    this.sources = [];
    this.strikePrice = [];
    this.premium = [];
    this.expiration = [];
    this.drop = [];
    this.dte = [];
    this.buyingPower = [];
    this.annularized = [];
    this.delta = [];

    if (this.scannerResults && this.scannerResults.length > 0) {
      this.scannerResults.forEach((d) => {
        if (d.atr_type === 'aggressive') {
          this.sources.push('A');
        } else if (d.atr_type === 'conservative') {
          this.sources.push('C');
        } else {
          this.sources.push(null);
        }

        this.strikePrice.push(new CustomTable(d.strike_price));
        this.delta.push(new CustomTable(d.delta));
        this.premium.push(new CustomTable(d.bid_price, d.min));
        this.dte.push(new CustomTable(d.dte));
        this.buyingPower.push(new CustomTable(d.strike_price * 100));
        this.annularized.push(new CustomTable(d.annularized));
        this.expiration.push(new CustomTable(moment(d.expiration).format('MMM D')));
        this.drop.push(new CustomTable(d.drop_percentage));
      });

      this.colGroupsByDate = this.getColGroups(this.expiration.map((exp) => exp.value));
    }
  }

  private async updateSymbolData(securityDetails: ISymbolDetailsV2WithSource): Promise<void> {
    if (!securityDetails) {
      return;
    }

    // set available data (last_traded_price and earnings) or set it to null
    this.lastTradePrice = securityDetails.last_traded_price;
    this.scannerService.lastTradePrice.set({
      security_id: securityDetails.security_id,
      price: securityDetails.last_traded_price,
    });

    if (this.subscribedStock) {
      this.liveDataService.unsubscribe(this.subscribedStock.subscriptionId, this.subscribedStock.symbol);
    }

    this.subscribedStock = {
      subscriptionId: uuidV4(),
      symbol: {
        symbol: securityDetails.symbol,
        country_code: ExchangeCountriesCodes.US,
      },
    };

    this.liveDataService.subscribe(
      this.subscribedStock.subscriptionId,
      this.subscribedStock.symbol,
      {},
      this.handleLiveDataCallback.bind(this),
    );

    // update last-traded-price from historical-data if it's from scannerV2
    if (securityDetails.isFromScanner) {
      try {
        const historicalData = await this.historicalDataService.get(securityDetails.security_id);

        this.lastTradePrice = historicalData?.length ? historicalData[historicalData.length - 1].close : null;
      } catch (error) {
        console.error(
          `WHEEL :: Something went wrong on fetching historical-data for security_id: ${securityDetails.security_id}, error: ${error}`,
        );
        this.lastTradePrice = null;
      }
    }
  }

  private handleLiveDataCallback(data): void {
    if (!data || data.symbol !== this.subscribedStock?.symbol?.symbol) {
      return;
    }

    if (!data.close || data.close <= 0) {
      return;
    }

    this.lastTradePrice = data.close;
  }

  private addSellPutOption(index: number): void {
    this.wheelService.onTriggerToSaveCalc$(true);
    this.saveSelectedTabToDB();

    this.securityDetails$.pipe(take(1)).subscribe((securityDetails) => {
      if (!securityDetails) {
        return;
      }

      const strikePrice = this.strikePrice[index].value;
      const premium = this.premium[index].value;
      const expiration = this.scannerResults[index].expiration;
      const stockPrice = this.lastTradePrice;
      const symbol = securityDetails.symbol;
      const uId = JSON.parse(localStorage.getItem('user')).userId;
      const delta = this.delta[index].value;
      const min = this.premium[index].value1;

      setTimeout(() => {
        this.save([uId, symbol, stockPrice, premium, strikePrice, expiration, delta, min, []]);
      }, 200);
    });
  }

  private getColGroups(list: string[]): number[] {
    const groups = list.reduce(
      (acc, value) => ({
        ...acc,
        [value]: (acc[value] || 0) + 1,
      }),
      {},
    );

    return Object.values(groups);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private save(data: any[]): void {
    this.wheelCalculatorService
      .saveStocksToDB({
        userId: data[0],
        symbol: data[1],
        last_traded_price: data[2],
        option_premium: data[3],
        strike_price: data[4],
        expiration: convertToEasternTime(data[5]).unix(),
        delta: data[6],
        min: data[7],
        data: data[8],
      })
      .subscribe(
        async (res) => {
          this.wheelService.setWheelCalcData(null);
          this.wheelService.setWheelDataWindow(null);
          this.scannerService.onSelectChart$.next(true);
          this.wheelCalculatorService.setRefreshStock$(res.result);
        },
        (error) => {
          if (error.error.description === 'Your wheel-calculate limit cross.') {
            this.showMaxColumnDialog();
          }
        },
      );
  }

  private saveSelectedTabToDB(): void {
    const viewMode = this.observableServiceV2.wheelViewMode.getValue();
    this.isMobile = this.width < this.mobileWidth;

    const currentTab = viewMode === WheelViewModes.Tabs || this.isMobile ? ChartTabs.Calculator : ChartTabs.Chart;

    this.observableServiceV2.WheelTab.next(currentTab);
    this.userDataService.set(UserSettings.WheelTab, currentTab);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private addFlagToDataObs(data) {
    let result;

    this.scannerService.smileyTrigger$.pipe(take(1)).subscribe((smiley) => {
      result = data.map((d) => {
        const dd = smiley.find((ddd) => ddd.security_id === d.security_id);

        return { flag: dd?.flag ?? Flags.None, ...d };
      });
    });

    return result;
  }

  private showMaxColumnDialog(): void {
    if (this.isMaxColumnDialogOpen) {
      return;
    }

    this.isMaxColumnDialogOpen = true;

    this.dialogsService
      .customConfirm({
        header: 'Adding a stock in the calculator',
        icon: 'info',
        confirmationText: 'Maximum allowed columns on The Wheel calculator is 20.',
        subText: 'Please remove not used columns and try again.',
        showCancel: false,
        okText: 'Ok',
      })
      .finally(() => {
        this.isMaxColumnDialogOpen = false;
      });
  }
}
